/**
 * CodeFilesPanel
 * Inline CRUD panel for managing (repo_name, glob_pattern) entries
 * on a given feature. Designed to handle millions of code file rows
 * via server-side pagination and search.
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';

const DEFAULT_LIMIT = 50;

function IconPlus() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="3 6 5 6 21 6"/>
      <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
      <path d="M10 11v6"/><path d="M14 11v6"/>
      <path d="M9 6V4h6v2"/>
    </svg>
  );
}

function IconEdit() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
    </svg>
  );
}

function IconCheck() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

function IconX() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
    </svg>
  );
}

const PROVIDER_COLORS = {
  github:    { bg: '#238636', text: '#fff' },
  gitlab:    { bg: '#e24329', text: '#fff' },
  bitbucket: { bg: '#0052cc', text: '#fff' },
  gitea:     { bg: '#609926', text: '#fff' },
};

/**
 * RepoAutocomplete – combo-box that queries /repositories as the user types.
 * Selecting a suggestion fills the input with the slug (last path segment).
 * Free-text entry is always allowed.
 */
function RepoAutocomplete({ value, onChange, placeholder, onKeyDown, style }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen]               = useState(false);
  const [loading, setLoading]         = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleChange(e) {
    const val = e.target.value;
    onChange(val);

    clearTimeout(debounceRef.current);
    if (!val.trim()) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await axios.get('/repositories', { params: { q: val.trim(), limit: 10 } });
        setSuggestions(res.data.data || []);
        setOpen(true);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 250);
  }

  function handleSelect(repo) {
    const slug = repo.repo_name.includes('/')
      ? repo.repo_name.split('/').pop()
      : repo.repo_name;
    onChange(slug);
    setSuggestions([]);
    setOpen(false);
  }

  function handleKeyDownInternal(e) {
    if (e.key === 'Escape') { setOpen(false); }
    if (onKeyDown) onKeyDown(e);
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', flex: 1 }}>
      <input
        className="form-input"
        placeholder={placeholder}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDownInternal}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        autoComplete="off"
        style={style}
      />
      {open && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
          background: 'var(--surface)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
          maxHeight: 240, overflowY: 'auto',
        }}>
          {loading && (
            <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)' }}>
              Loading…
            </div>
          )}
          {suggestions.map(repo => {
            const colors = PROVIDER_COLORS[repo.provider] || { bg: 'var(--border)', text: 'var(--text-primary)' };
            return (
              <div
                key={repo.id}
                onMouseDown={() => handleSelect(repo)}
                style={{
                  padding: '7px 12px', cursor: 'pointer', fontSize: 13,
                  display: 'flex', alignItems: 'center', gap: 8,
                  borderBottom: '1px solid var(--border-subtle)',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-hover)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: '1px 6px',
                  borderRadius: 999, background: colors.bg, color: colors.text,
                  textTransform: 'capitalize', flexShrink: 0,
                }}>
                  {repo.provider}
                </span>
                <span style={{ color: 'var(--text-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {repo.repo_name}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function CodeFilesPanel({ featureId }) {
  const [rows, setRows]         = useState([]);
  const [total, setTotal]       = useState(0);
  const [page, setPage]         = useState(1);
  const [pages, setPages]       = useState(1);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  // Search
  const [repoFilter, setRepoFilter]   = useState('');
  const [globFilter, setGlobFilter]   = useState('');
  const [appliedRepo, setAppliedRepo] = useState('');
  const [appliedGlob, setAppliedGlob] = useState('');

  // Add form
  const [showAdd, setShowAdd]   = useState(false);
  const [newRepo, setNewRepo]   = useState('');
  const [newGlob, setNewGlob]   = useState('');
  const [addError, setAddError] = useState('');
  const [adding, setAdding]     = useState(false);

  // Bulk add textarea
  const [bulkMode, setBulkMode]   = useState(false);
  const [bulkText, setBulkText]   = useState('');
  const [bulkError, setBulkError] = useState('');

  // Inline edit
  const [editingId, setEditingId]     = useState(null);
  const [editRepo, setEditRepo]       = useState('');
  const [editGlob, setEditGlob]       = useState('');
  const [editSaving, setEditSaving]   = useState(false);

  const fetchData = useCallback(async (repo, glob, pageNum) => {
    setLoading(true);
    setError('');
    try {
      const params = { page: pageNum, limit: DEFAULT_LIMIT };
      if (repo) params.repo = repo;
      if (glob) params.glob = glob;
      const res = await axios.get(`/features/${featureId}/code-files`, { params });
      setRows(res.data.data);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load code files');
    } finally {
      setLoading(false);
    }
  }, [featureId]);

  useEffect(() => {
    fetchData('', '', 1);
  }, [fetchData]);

  function handleApplySearch() {
    setAppliedRepo(repoFilter);
    setAppliedGlob(globFilter);
    setPage(1);
    fetchData(repoFilter, globFilter, 1);
  }

  function handleClearSearch() {
    setRepoFilter('');
    setGlobFilter('');
    setAppliedRepo('');
    setAppliedGlob('');
    setPage(1);
    fetchData('', '', 1);
  }

  function handleSearchKeyDown(e) {
    if (e.key === 'Enter') handleApplySearch();
  }

  function handlePageChange(newPage) {
    setPage(newPage);
    fetchData(appliedRepo, appliedGlob, newPage);
  }

  // ── Add single ────────────────────────────────────────────────────────────
  async function handleAddSingle() {
    if (!newRepo.trim() || !newGlob.trim()) {
      setAddError('Both repo name and glob pattern are required');
      return;
    }
    setAdding(true);
    setAddError('');
    try {
      await axios.post(`/features/${featureId}/code-files`, {
        repo_name: newRepo.trim(),
        glob_pattern: newGlob.trim(),
      });
      setNewRepo('');
      setNewGlob('');
      setShowAdd(false);
      setPage(1);
      fetchData(appliedRepo, appliedGlob, 1);
    } catch (e) {
      setAddError(e.response?.data?.error || 'Add failed');
    } finally {
      setAdding(false);
    }
  }

  // ── Add bulk ──────────────────────────────────────────────────────────────
  async function handleAddBulk() {
    const lines = bulkText.split('\n').map(l => l.trim()).filter(Boolean);
    const entries = [];
    const parseErrors = [];
    lines.forEach((line, i) => {
      const parts = line.split(/\s*,\s*|\s+/);
      if (parts.length < 2) {
        parseErrors.push(`Line ${i + 1}: expected "repo_name  glob_pattern"`);
        return;
      }
      entries.push({ repo_name: parts[0], glob_pattern: parts.slice(1).join(' ') });
    });
    if (parseErrors.length) { setBulkError(parseErrors.join('\n')); return; }
    if (!entries.length) { setBulkError('No valid lines found'); return; }

    setAdding(true);
    setBulkError('');
    try {
      await axios.post(`/features/${featureId}/code-files`, entries);
      setBulkText('');
      setBulkMode(false);
      setShowAdd(false);
      setPage(1);
      fetchData(appliedRepo, appliedGlob, 1);
    } catch (e) {
      setBulkError(e.response?.data?.error || 'Bulk add failed');
    } finally {
      setAdding(false);
    }
  }

  // ── Inline edit ───────────────────────────────────────────────────────────
  function startEdit(row) {
    setEditingId(row.id);
    setEditRepo(row.repo_name);
    setEditGlob(row.glob_pattern);
  }

  function cancelEdit() {
    setEditingId(null);
  }

  async function saveEdit(id) {
    if (!editRepo.trim() || !editGlob.trim()) return;
    setEditSaving(true);
    try {
      await axios.put(`/code-files/${id}`, {
        repo_name: editRepo.trim(),
        glob_pattern: editGlob.trim(),
      });
      setEditingId(null);
      fetchData(appliedRepo, appliedGlob, page);
    } catch (e) {
      alert(e.response?.data?.error || 'Update failed');
    } finally {
      setEditSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this code file entry?')) return;
    try {
      await axios.delete(`/code-files/${id}`);
      fetchData(appliedRepo, appliedGlob, page);
    } catch (e) {
      alert(e.response?.data?.error || 'Delete failed');
    }
  }

  const hasSearch = appliedRepo || appliedGlob;

  return (
    <div>
      {/* Panel header */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 14,
      }}>
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>
            Implementing Code Files
          </h3>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {total.toLocaleString()} entr{total !== 1 ? 'ies' : 'y'}
          </div>
        </div>
        <button
          className="btn btn-primary"
          style={{ fontSize: 12 }}
          onClick={() => { setShowAdd(s => !s); setAddError(''); setBulkError(''); }}
        >
          <IconPlus /> Add Files
        </button>
      </div>

      {/* Add form */}
      {showAdd && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <button
              className={`btn ${!bulkMode ? 'btn-primary' : 'btn-secondary'}`}
              style={{ fontSize: 12 }}
              onClick={() => setBulkMode(false)}
            >
              Single
            </button>
            <button
              className={`btn ${bulkMode ? 'btn-primary' : 'btn-secondary'}`}
              style={{ fontSize: 12 }}
              onClick={() => setBulkMode(true)}
            >
              Bulk (CSV / whitespace)
            </button>
          </div>

          {!bulkMode ? (
            <>
              <div className="form-row" style={{ marginBottom: 10 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Repo Name</label>
                  <RepoAutocomplete
                    value={newRepo}
                    onChange={setNewRepo}
                    placeholder="my-repo"
                    onKeyDown={e => e.key === 'Enter' && handleAddSingle()}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Glob Pattern</label>
                  <input className="form-input" placeholder="src/main/**/*.java"
                    value={newGlob} onChange={e => setNewGlob(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleAddSingle()} />
                </div>
              </div>
              {addError && <div className="error-msg" style={{ marginBottom: 10 }}>{addError}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" style={{ fontSize: 12 }}
                  disabled={adding} onClick={handleAddSingle}>
                  {adding ? 'Adding…' : 'Add'}
                </button>
                <button className="btn btn-ghost" style={{ fontSize: 12 }}
                  onClick={() => setShowAdd(false)}>
                  Cancel
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="form-group" style={{ marginBottom: 10 }}>
                <label className="form-label">
                  One entry per line: <code style={{ fontSize: 11, color: 'var(--info)' }}>repo_name  glob_pattern</code>
                </label>
                <textarea
                  className="form-input"
                  rows={6}
                  placeholder={"my-repo  src/main/**/*.java\nother-repo  lib/**/*.ts"}
                  value={bulkText}
                  onChange={e => setBulkText(e.target.value)}
                  style={{ fontFamily: 'monospace', fontSize: 12, resize: 'vertical' }}
                />
              </div>
              {bulkError && (
                <pre className="error-msg" style={{ whiteSpace: 'pre-wrap', marginBottom: 10 }}>
                  {bulkError}
                </pre>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary" style={{ fontSize: 12 }}
                  disabled={adding} onClick={handleAddBulk}>
                  {adding ? 'Adding…' : 'Add All'}
                </button>
                <button className="btn btn-ghost" style={{ fontSize: 12 }}
                  onClick={() => setShowAdd(false)}>
                  Cancel
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Search bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          className="form-input"
          style={{ width: 200 }}
          placeholder="Filter by repo…"
          value={repoFilter}
          onChange={e => setRepoFilter(e.target.value)}
          onKeyDown={handleSearchKeyDown}
        />
        <input
          className="form-input"
          style={{ width: 240 }}
          placeholder="Filter by glob (e.g. **/*.java)…"
          value={globFilter}
          onChange={e => setGlobFilter(e.target.value)}
          onKeyDown={handleSearchKeyDown}
        />
        <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={handleApplySearch}>
          Search
        </button>
        {hasSearch && (
          <button className="btn btn-ghost" style={{ fontSize: 12, color: 'var(--danger)' }}
            onClick={handleClearSearch}>
            Clear
          </button>
        )}
      </div>

      {error && <div className="error-msg">{error}</div>}

      {/* Table */}
      <div className="table-wrap" style={{ position: 'relative' }}>
        {loading && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 5, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: 'rgba(15,23,42,0.5)',
          }}>
            <div className="spinner" />
          </div>
        )}
        <table>
          <thead>
            <tr>
              <th>Repo Name</th>
              <th>Glob Pattern</th>
              <th>Added</th>
              <th style={{ width: 90 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={4}>
                  <div className="empty-state" style={{ padding: '30px 20px' }}>
                    <div className="icon">📁</div>
                    <h3>No code files</h3>
                    <p>{hasSearch ? 'No entries match your search' : 'Add code file entries above'}</p>
                  </div>
                </td>
              </tr>
            )}
            {rows.map(row => (
              <tr key={row.id}>
                {editingId === row.id ? (
                  <>
                    <td>
                      <RepoAutocomplete
                        value={editRepo}
                        onChange={setEditRepo}
                        placeholder="repo-name"
                        style={{ fontSize: 12 }}
                      />
                    </td>
                    <td>
                      <input className="form-input" style={{ fontFamily: 'monospace', fontSize: 12 }}
                        value={editGlob} onChange={e => setEditGlob(e.target.value)} />
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                      {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-ghost" style={{ color: 'var(--success)' }}
                          disabled={editSaving} onClick={() => saveEdit(row.id)}>
                          <IconCheck />
                        </button>
                        <button className="btn btn-ghost" onClick={cancelEdit}>
                          <IconX />
                        </button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td style={{ color: 'var(--info)', fontWeight: 500 }}>{row.repo_name}</td>
                    <td>
                      <code style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-primary)' }}>
                        {row.glob_pattern}
                      </code>
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                      {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button className="btn btn-ghost" onClick={() => startEdit(row)}
                          title="Edit">
                          <IconEdit />
                        </button>
                        <button className="btn btn-ghost" style={{ color: 'var(--danger)' }}
                          onClick={() => handleDelete(row.id)} title="Delete">
                          <IconTrash />
                        </button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pages > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginTop: 12, fontSize: 12, color: 'var(--text-secondary)',
        }}>
          <span>
            {(page - 1) * DEFAULT_LIMIT + 1}–{Math.min(page * DEFAULT_LIMIT, total)} of {total.toLocaleString()}
          </span>
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: 12 }}
              disabled={page <= 1} onClick={() => handlePageChange(page - 1)}>
              ‹ Prev
            </button>
            <span style={{ padding: '4px 8px' }}>{page} / {pages}</span>
            <button className="btn btn-secondary" style={{ padding: '4px 8px', fontSize: 12 }}
              disabled={page >= pages} onClick={() => handlePageChange(page + 1)}>
              Next ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
